import { LocalDeck, useCanTakeActions } from "@/lib/decks-utils/utils";
import { useAuth } from "@clerk/react-router";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Folder, localDB } from "@/database/db";

import {
  ArrowUpDownIcon,
  EditIcon,
  HomeIcon,
  ImageIcon,
  Loader2Icon,
  PaletteIcon,
  PlusIcon,
  SaveIcon,
  WandSparklesIcon,
  XIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { TooltipWithShortcuts } from "@/components/tooltips";
import { getCustomizedColor } from "../folder/folder-color-utils";
import { FullFoldersSubtree } from "../sidebar/sidebar";
import { usePostHog } from "posthog-js/react";

import {
  ColorPicker,
  ColorPickerFormat,
  ColorPickerEyeDropper,
  ColorPickerHue,
  ColorPickerSelection,
} from "../ui/color-picker";
import Color from "color";

export const COLOR_CLASSES = {
  violet: "bg-violet-400 text-violet-950 border-violet-950",
  yellow: "bg-yellow-400 text-yellow-950 border-yellow-950",
  orange: "bg-orange-400 text-orange-950 border-orange-950",
  blue: "bg-blue-400 text-blue-950 border-blue-950",
  sky: "bg-sky-400 text-sky-950 border-sky-950",
  teal: "bg-teal-400 text-teal-950 border-teal-950",
  green: "bg-green-400 text-green-950 border-green-950",
  lime: "bg-lime-400 text-lime-950 border-lime-950",
};

export const COLORS_BORDER_SIDEBAR = [
  "border-orange-300",
  "border-amber-300",
  "border-lime-300",
  "border-green-300",
  "border-emerald-300",
  "border-teal-300",
  "border-cyan-300",
  "border-sky-300",
  "border-blue-300",
  "border-fuchsia-300",
  "border-pink-300",
  "border-rose-300",
  "border-gray-300",
] as const;

export const COLORS = [
  "orange",
  "amber",
  "lime",
  "green",
  "emerald",
  "teal",
  "cyan",
  "sky",
  "blue",
  "fuchsia",
  "pink",
  "rose",
  "gray", // Special color for decks without folders!
] as const;

export const EditDialog = ({ deck }: { deck: LocalDeck }) => {
  const [showEditDialog, setShowEditDialog] = useState(false);

  return (
    <>
      <Button
        size="md"
        variant="secondary"
        onClick={() => setShowEditDialog(true)}
      >
        <EditIcon className="w-icon-sm h-icon-sm" />
        <span className="hidden md:flex">Edit</span>
      </Button>
      {showEditDialog ? (
        <DeckEditingDialog
          deckToEdit={deck}
          close={() => setShowEditDialog(false)}
        />
      ) : (
        <></>
      )}
    </>
  );
};

const MAX_SIZE_THUMBAILTEXT = 8;
export const DeckEditingDialog = ({
  deckToEdit,
  close,
  children,
}: {
  deckToEdit?: LocalDeck;
  close: () => void;
  children?: React.ReactNode;
}) => {
  const canTakeAction = useCanTakeActions();
  const { userId } = useAuth();
  const posthog = usePostHog();

  return (
    <Dialog open={!!deckToEdit} onOpenChange={() => close()}>
      {children ? children : <></>}
      {deckToEdit ? (
        <DialogContent className="max-w-[730px] max-h-[80dvh] p-0 gap-0">
          <DialogHeader className="flex gap-2 items-center justify-start flex-row text-title-950 font-semibold p-5 shrink-0 border-b border-secondary-200">
            <EditIcon className="h-icon-lg w-icon-lg" />
            Edit deck
          </DialogHeader>
          <div className="overflow-y-auto flex-1 px-1">
            <DeckEditingForm
              onClose={close}
              disabled={!canTakeAction}
              initalThumbnailText={
                deckToEdit.thumbnailText ??
                (deckToEdit.displayName?.slice(0, 8) || "")
              }
              initialImageBackground={
                deckToEdit ? deckToEdit.imageBackground || "" : ""
              }
              initialName={deckToEdit ? deckToEdit.displayName || "" : ""}
              onSubmit={async (values) => {
                await localDB.updateDeckProperties({
                  displayName: values.displayName,
                  color: values.color,
                  thumbnailText: values.thumbnailText,
                  imageBackground: values.imageBackground,
                  name: deckToEdit.name,
                  folder: deckToEdit.folder,
                  languageSettings: deckToEdit.languageSettings,
                });
                posthog.capture("Deck-Edit-Properties-Confirm", {
                  userId: userId || "not-loaded",
                  deckName: deckToEdit.name,
                });
                close();
              }}
              initColor={deckToEdit?.color || COLORS[0]}
              confirmLabel={"Save Changes"}
            />
          </div>
          <DialogFooter className="p-4 shrink-0 border-t border-secondary-200">
            <div className="w-full flex justify-end gap-3">
              <Button type="button" size="lg" onClick={close}>
                <XIcon className="h-5 w-5" />
                Cancel
              </Button>
              <Button
                type="submit"
                form="edit-deck-form"
                variant="primary"
                size="lg"
                disabled={!canTakeAction}
              >
                <SaveIcon className="h-5 w-5" />
                Save Changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      ) : (
        <></>
      )}
    </Dialog>
  );
};

export const MoveDeckFolderDialog = ({
  deckToMove,
  folders,
  close,
  children,
}: {
  deckToMove?: LocalDeck;
  folders: Folder[];
  close: () => void;
  children?: React.ReactNode;
}) => {
  const [selectedFolder, setSelectedFolder] = useState(
    deckToMove?.folder || ""
  );

  return (
    <Dialog open={!!deckToMove} onOpenChange={() => close()}>
      {children ? children : <></>}
      {deckToMove ? (
        <>
          <DialogContent className="max-h-[90dvh] pb-12">
            <div className="items-center justify-center text-subtitle-800 max-h-[80dvh] overflow-y-auto">
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-0 h-full overflow-y-auto">
                  <Button
                    variant="strokeSecondarySidebar"
                    size="sm"
                    className={cn(
                      "w-full p-2 my-0.5 text-xs items-center justify-start flex-shrink-0"
                    )}
                    onClick={() => setSelectedFolder("")}
                  >
                    <HomeIcon className="mr-1" />
                    Home
                  </Button>
                  <FullFoldersSubtree
                    folders={folders}
                    localDecks={[]}
                    folderId={selectedFolder}
                    onClickFolder={(folderId) => {
                      setSelectedFolder(folderId || "");
                    }}
                    hideDecks
                    expandAll
                  />
                </div>
              </div>
            </div>
            <div className="fixed bottom-0 h-14 w-full bg-secondary-50 z-50 flex items-center justify-center">
              <MoveDeckConfirmationButton
                deckToMove={deckToMove}
                newFolder={selectedFolder}
                closeDialog={() => close()}
              />
            </div>
          </DialogContent>
        </>
      ) : (
        <></>
      )}
    </Dialog>
  );
};

const MoveDeckConfirmationButton = ({
  deckToMove,
  newFolder,
  closeDialog,
}: {
  deckToMove: LocalDeck;
  newFolder: Folder["id"];
  children?: React.ReactNode;
  closeDialog: () => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);

  return (
    <Button
      type="submit"
      variant="primary"
      disabled={newFolder === deckToMove.folder || isLoading}
      onClick={async () => {
        setIsLoading(true);
        await localDB.updateDeckProperties({
          ...deckToMove,
          folder: newFolder,
        });
        closeDialog();
        setIsLoading(false);
      }}
    >
      {isLoading ? (
        <Loader2Icon className="h-5 w-5 animate-spin" />
      ) : (
        <>
          <ArrowUpDownIcon className="h-5 w-5 mr-1" />
          Move Deck
        </>
      )}
    </Button>
  );
};

const IMAGE_COLOR_MAP: Record<string, string> = {
  "pixel-art/1.png": "emerald",
  "pixel-art/2.png": "teal",
  "pixel-art/3.png": "blue",
  "pixel-art/4.png": "cyan",
  "pixel-art/5.png": "fuchsia",
  "pixel-art/6.png": "fuchsia",
  "pixel-art/7.png": "fuchsia",
  "pixel-art/8.png": "sky",

  "life-science/1.png": "rose",
  "life-science/2.png": "amber",
  "life-science/3.png": "rose",
  "life-science/4.png": "rose",
  "life-science/5.png": "grey",
  "life-science/6.png": "amber",
  "life-science/7.png": "cyan",
  "life-science/8.png": "fuchsia",

  "nature-animals/1.png": "orange",
  "nature-animals/2.png": "amber",
  "nature-animals/3.png": "lime",
  "nature-animals/4.png": "green",
  "nature-animals/5.png": "emerald",
  "nature-animals/6.png": "lime",
  "nature-animals/7.png": "teal",
  "nature-animals/8.png": "sky",
};

const ImageCollection = ({
  name,
  id,
  setSelectedImageBackground,
  setSelectedColor,
  selectedImageBackground,
}: {
  name: string;
  id: string;
  selectedImageBackground: string;
  setSelectedColor: (color: string) => void;
  setSelectedImageBackground: (cover: string) => void;
}) => (
  <div className="flex flex-col gap-2">
    <Label htmlFor="thumbnail" className="font-semibold text-xs">
      {name}
    </Label>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {Array.from({ length: 8 }).map((_, index) => {
        const cover = `/images/deck-cover/${id}/${index + 1}.png`;
        const color = IMAGE_COLOR_MAP[`${id}/${index + 1}.png`] || "gray";

        return (
          <div
            key={index}
            onClick={() => {
              setSelectedImageBackground(cover);
              setSelectedColor(color);
            }}
          >
            <img
              src={cover}
              alt="Deck cover"
              className={cn(
                `w-full rounded-xl cursor-pointer border-2 border-secondary-200`,
                selectedImageBackground === cover && "border-secondary-500"
              )}
            />
          </div>
        );
      })}
    </div>
  </div>
);

export const DeckEditingForm = ({
  initialName,
  initalThumbnailText,
  initialImageBackground,
  initColor,
  isDialog = true,
  onSubmit,
  onChange,
}: {
  initialName: string;
  initalThumbnailText: string;
  initialImageBackground: string;
  initColor?: string;
  isDialog?: boolean;
  disabled: boolean;
  onSubmit: (value: {
    displayName: string;
    color: string;
    thumbnailText: string;
    imageBackground: string;
  }) => void;
  onChange?: (value: {
    displayName: string;
    color: string;
    thumbnailText: string;
    imageBackground: string;
  }) => void;
  onClose?: () => void;
  isLoading?: boolean;
  confirmLabel: string;
}) => {
  const { userId } = useAuth();
  const posthog = usePostHog();
  const [newName, setNewName] = useState(initialName);
  const [thumbnailText, setThumbnailText] = useState(initalThumbnailText);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const pathname = useLocation().pathname;
  // Exception: on mobile we don't show tag personalization in the /add page to reduce confusion
  const stylingCoverTag = pathname === "/add" ? "hidden md:flex" : "flex";

  const [deckCoverType, setDeckCoverType] = useState<"color" | "gallery">(
    "color"
  );

  const [selectedColor, setSelectedColor] = useState<string>(
    initColor || COLORS[0]
  );

  const [selectedImageBackground, setSelectedImageBackground] = useState(
    initialImageBackground
  );

  useEffect(() => {
    if (onChange) {
      onChange({
        displayName: newName,
        color: selectedColor,
        thumbnailText: thumbnailText,
        imageBackground: selectedImageBackground,
      });
    }
  }, [newName, thumbnailText, selectedColor, selectedImageBackground]);
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        onSubmit({
          displayName: newName,
          color: selectedColor,
          thumbnailText: thumbnailText,
          imageBackground: selectedImageBackground,
        });
        posthog.capture("Deck-Create-Edit-Details", {
          userId: userId || "not-loaded",
          deckName: newName,
          color: selectedColor,
          thumbnailText: thumbnailText,
          imageBackground: selectedImageBackground,
        });
      }}
      id="edit-deck-form"
      className={`flex flex-col gap-8 ${isDialog ? "p-6" : "pb-6"}`}
    >
      <div className="flex flex-col gap-2">
        <Label htmlFor="name" className="font-semibold">
          Name
        </Label>
        <Input
          id="name"
          value={newName}
          onChange={(e) => {
            setNewName(e.target.value);
          }}
          placeholder="My new Deck! ⚡️"
          className="text-base"
        />
      </div>
      <div className={cn("flex flex-col gap-2", stylingCoverTag)}>
        <Label htmlFor="customization" className="font-semibold">
          Cover Emoji/Tag
        </Label>
        <div className="relative">
          <Input
            id="customization"
            value={thumbnailText}
            onChange={(e) => {
              setThumbnailText(e.target.value.slice(0, MAX_SIZE_THUMBAILTEXT));
            }}
            maxLength={MAX_SIZE_THUMBAILTEXT}
            placeholder="⚡️ Deck"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-5 pointer-events-none text-sm text-gray-500 text-right font-semibold">
            {thumbnailText.length}/{MAX_SIZE_THUMBAILTEXT}
          </div>
        </div>
      </div>

      <div>
        <Label htmlFor="thumbnail" className="font-semibold">
          Deck Cover
        </Label>
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="tileSecondary"
            size="tileXlg"
            className={cn(
              deckCoverType === "color" &&
                "border bg-secondary-100 border-secondary-500 text-secondary-950"
            )}
            onClick={(e) => {
              e.preventDefault();
              setDeckCoverType("color");
            }}
          >
            <PaletteIcon className="h-5 w-5" />
            Colours
            <div className="font-normal text-xs whitespace-normal overflow-visible hidden md:block">
              Choose a colour from our selected palette or add your own
            </div>
          </Button>
          <Button
            variant="tileSecondary"
            size="tileXlg"
            className={cn(
              deckCoverType === "gallery" &&
                "border bg-secondary-100 border-secondary-500 text-secondary-950"
            )}
            onClick={(e) => {
              e.preventDefault();
              posthog.capture("Gallery-Tile-Button");
              setDeckCoverType("gallery");
            }}
          >
            <ImageIcon className="h-5 w-5" />
            Gallery
            <div className="font-normal text-xs whitespace-normal overflow-visible hidden md:block">
              Choose a photo from our selected collection
            </div>
          </Button>
        </div>
      </div>

      {deckCoverType === "color" && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="thumbnail" className="font-semibold">
            Color
          </Label>

          <Dialog open={showColorPicker} onOpenChange={setShowColorPicker}>
            <DialogContent
              showCloseButton={false}
              forceMount
              className="absolute top-[45%] xs:left-[50%] sm:left-[30%] w-full max-w-[300px] !rounded-2xl border bg-secondary-50 p-4 shadow-sm"
            >
              <ColorPicker
                onChange={(value) => {
                  setSelectedColor(Color.rgb(value).hex().toString());
                  setSelectedImageBackground("");
                }}
              >
                <ColorPickerSelection />
                <div className="flex items-center gap-4">
                  <ColorPickerEyeDropper />
                  <div className="w-full grid gap-1">
                    <ColorPickerHue />
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-3">
                    <Badge
                      className="h-full px-3 font-normal"
                      variant="secondary"
                    >
                      HEX
                    </Badge>
                  </div>
                  <div className="col-span-9">
                    <ColorPickerFormat />
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => {
                    setShowColorPicker(false);
                    posthog.capture("Add-Colour-Button");
                  }}
                >
                  <PlusIcon className="h-5 w-5" />
                  Add colour
                </Button>
              </ColorPicker>
            </DialogContent>
          </Dialog>
          <div className="grid grid-cols-5 md:grid-cols-6 gap-3">
            {COLORS.slice(0, -1).map((color, index) => (
              <TooltipWithShortcuts
                key={index}
                text={color}
                className="capitalize"
              >
                <div
                  key={index}
                  className={cn(
                    `w-full h-16 rounded-xl cursor-pointer`,
                    getCustomizedColor(color),
                    selectedColor === color
                      ? `border-2 border-secondary-700`
                      : ""
                  )}
                  onClick={() => {
                    setSelectedColor(color);
                    setSelectedImageBackground("");
                  }}
                />
              </TooltipWithShortcuts>
            ))}
            {!COLORS.includes(selectedColor as (typeof COLORS)[number]) && (
              <TooltipWithShortcuts
                key={15}
                text={selectedColor}
                className="capitalize"
              >
                <div
                  key={15}
                  style={{ backgroundColor: selectedColor }}
                  className={cn(
                    `w-full h-16 rounded-xl cursor-pointer`,
                    `border-2 border-secondary-700`
                  )}
                />
              </TooltipWithShortcuts>
            )}
            <Button
              size="sm"
              className={cn("h-16", "border-2 rounded-xl")}
              onClick={(e) => {
                e.preventDefault();
                setShowColorPicker(true);
              }}
            >
              <PlusIcon className="h-6 w-6" />
            </Button>
          </div>
        </div>
      )}

      {deckCoverType === "gallery" && (
        <>
          <ImageCollection
            name="Life Science"
            id="life-science"
            setSelectedColor={setSelectedColor}
            selectedImageBackground={selectedImageBackground}
            setSelectedImageBackground={setSelectedImageBackground}
          />
          <ImageCollection
            name="Nature & Animals"
            id="nature-animals"
            setSelectedColor={setSelectedColor}
            selectedImageBackground={selectedImageBackground}
            setSelectedImageBackground={setSelectedImageBackground}
          />
          <ImageCollection
            name="Pixel Art"
            id="pixel-art"
            setSelectedColor={setSelectedColor}
            selectedImageBackground={selectedImageBackground}
            setSelectedImageBackground={setSelectedImageBackground}
          />
        </>
      )}
    </form>
  );
};

export type DeckCreationEditingFormProps = {
  displayName: string;
  color: string;
  thumbnailText: string;
  imageBackground: string;
  folder: string;
};
export const DeckCreationEditingForm = ({
  onSubmit,
  isLoading,
}: {
  onSubmit: (params: DeckCreationEditingFormProps) => void;
  isLoading: boolean;
}) => {
  const canTakeAction = useCanTakeActions();

  const [colorValue, setColorValue] = useState<string>(COLORS[0]);
  const [imageValue, setImageValue] = useState<string>("");
  const [displayName, setDisplayName] = useState<string>("New Deck [#]");
  const [thumbnailText, setThumbnailText] = useState<string>("");
  const [params] = useSearchParams();
  const folderId = params.get("folder") as string | null;

  const backgroundStyle =
    colorValue.startsWith("#") && !imageValue
      ? { backgroundColor: colorValue || "violet" }
      : { backgroundImage: `url('${imageValue}')`, backgroundSize: "contain" };

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-10 w-full">
        <DeckEditingForm
          disabled={!canTakeAction || !displayName}
          initalThumbnailText={thumbnailText}
          initialName={displayName}
          initialImageBackground={imageValue}
          isDialog={false}
          onSubmit={() =>
            onSubmit({
              color: colorValue,
              displayName: displayName,
              thumbnailText: thumbnailText,
              imageBackground: imageValue,
              folder: folderId || "",
            })
          }
          onChange={(value) => {
            setColorValue(value.color);
            setThumbnailText(value.thumbnailText);
            setImageValue(value.imageBackground);
            setDisplayName(value.displayName);
          }}
          initColor={colorValue}
          isLoading={isLoading}
          confirmLabel={"Create Deck"}
        />
        <div className="justify-items-end border-l border-secondary-200">
          <div
            className={cn(
              `hidden md:block w-64 h-64 relative border-2 border-secondary-200 transition-all rounded-3xl overflow-hidden group`,
              colorValue.startsWith("#") ? "" : getCustomizedColor(colorValue)
            )}
            style={backgroundStyle}
          >
            <div className="md:h-40 w-full flex items-center justify-center text-title-950 text-4xl font-bold">
              {thumbnailText}
            </div>
            <div className="md:h-24 bg-secondary-50 text-subtitle-800 w-full p-3 flex flex-col gap-3 px-4">
              <div className="whitespace-nowrap">{displayName}</div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={cn(
          "flex border-t border-secondary-200 bg-secondary-50",
          "md:p-0 md:pt-6 md:pr-6 md:-mx-10 md:w-120% md:justify-end md:relative md:w-auto",
          "fixed bottom-0 right-0 left-0 w-dvw p-4 z-50 items-center justify-center"
        )}
      >
        <Button
          type="submit"
          form="edit-deck-form"
          variant="primary"
          size="lg"
          className="w-full md:w-auto"
          disabled={!canTakeAction}
        >
          <WandSparklesIcon className="h-5 w-5" />
          Continue
        </Button>
      </div>
    </div>
  );
};
